// La cadencia de una mantención vive en el backend como `cadenceDays`
// (ADR-0083 Decision 2: un solo ciclo fijo para todas las tiendas). El
// handoff la muestra y la edita como «Cada N + Unidad» (Semanas / Meses /
// Años); estas funciones son la ida y vuelta entre ambas formas, con la
// convención del catálogo (90 = trimestral, 365 = anual): un mes son 30
// días y un año 365. Puras, sin fechas: la «Próxima» por tienda la deriva
// core-engine (maintenanceDueState), no este archivo.

export type CadenceUnit = "weeks" | "months" | "years" | "days";

export const CADENCE_UNIT_DAYS: Record<CadenceUnit, number> = {
  weeks: 7,
  months: 30,
  years: 365,
  days: 1,
};

// Las unidades que ofrece el formulario, en el orden del prototipo
// (Meses · Semanas · Años; Meses es la opción por defecto de «Nueva»).
// «Días» no está en el handoff: sólo aparece cuando la cadencia guardada
// no es múltiplo de semanas, meses ni años (cargas por planilla), para no
// mostrar un valor distinto del real.
export const CADENCE_FORM_UNITS: readonly CadenceUnit[] = ["months", "weeks", "years"];

export const CADENCE_UNIT_LABELS: Record<CadenceUnit, string> = {
  weeks: "Semanas",
  months: "Meses",
  years: "Años",
  days: "Días",
};

export type CadenceParts = { every: number; unit: CadenceUnit };

// Prefiere la unidad más grande que divide exacto, así 365 se lee «1 año»
// y no «52 semanas». La descomposición es exacta en ambos sentidos
// (composeCadence(decomposeCadence(d)) === d): 360 se lee «12 meses», no
// «1 año», porque un año compone a 365 y el drawer Editar compara lo
// compuesto contra lo guardado para habilitar Guardar — una promoción
// inexacta lo habilitaría sin que el gerente tocara nada.
export function decomposeCadence(cadenceDays: number): CadenceParts {
  if (cadenceDays > 0 && cadenceDays % 365 === 0)
    return { every: cadenceDays / 365, unit: "years" };
  if (cadenceDays > 0 && cadenceDays % 30 === 0) return { every: cadenceDays / 30, unit: "months" };
  if (cadenceDays > 0 && cadenceDays % 7 === 0) return { every: cadenceDays / 7, unit: "weeks" };
  return { every: cadenceDays, unit: "days" };
}

export function composeCadence(parts: CadenceParts): number {
  return parts.every * CADENCE_UNIT_DAYS[parts.unit];
}

// «Cada» acepta sólo enteros positivos; el backend exige
// cadenceDays entero > 0 y esto lo refleja antes del 400.
export function isValidEvery(raw: string): boolean {
  return /^\d+$/.test(raw.trim()) && Number(raw) > 0;
}

const NAMED_FREQUENCIES: Record<string, string> = {
  "1 weeks": "Semanal",
  "2 weeks": "Quincenal",
  "1 months": "Mensual",
  "2 months": "Bimestral",
  "3 months": "Trimestral",
  "6 months": "Semestral",
  "1 years": "Anual",
};

const UNIT_WORDS: Record<CadenceUnit, [string, string]> = {
  weeks: ["semana", "semanas"],
  months: ["mes", "meses"],
  years: ["año", "años"],
  days: ["día", "días"],
};

// La columna Frecuencia: el nombre usual cuando existe («Trimestral»),
// si no «Cada 5 semanas».
export function frequencyLabel(cadenceDays: number): string {
  const parts = decomposeCadence(cadenceDays);
  const named = NAMED_FREQUENCIES[`${parts.every} ${parts.unit}`];
  if (named) return named;
  const [one, many] = UNIT_WORDS[parts.unit];
  return `Cada ${parts.every} ${parts.every === 1 ? one : many}`;
}
